<template>
  <v-container>
    <the-header :title="editMode ? getLabel('edit_permissions') : getLabel('create_permission')" :go-back="goBack" />
    <v-card v-if="!loadingForm">
      <v-sheet elevation="3" class="rounded-lg rounded-b-0" color="primary">
        <v-card-title class="text-h6 white--text">{{ editMode ? getLabel('edit_permissions') : getLabel('create_permission') }}</v-card-title>
      </v-sheet>
      <v-divider />
      <v-card-text>
        <v-alert v-if="errors.length" type="error" class="rounded-lg" dismissible @input="closeAlert">
          <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
        </v-alert>
        <v-alert v-if="successes.length" type="success" class="rounded-lg" dismissible @input="closeAlert">
          <div v-for="(success, index) in successes" :key="index">{{ success }}</div>
        </v-alert>
        <v-stepper v-model="stepper" alt-labels>
          <v-stepper-header>
            <template v-for="(step, stepNumber ) in steps">
              <v-stepper-step :key="`${step}_step`" :step="stepNumber+1" :complete="stepNumber+1 < stepper" class="text-center">{{ getLabel(step) }}</v-stepper-step>
              <v-divider v-if="steps.length > stepNumber+1" :key="`${step}_divider`" />
            </template>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content v-for="(step, stepNumber ) in steps" :key="`${step}_content`" :step="stepNumber+1">
              <SelectPermission
                v-if="step === 'SELECT_PERMISSION'"
                :restriction="restrictionForm"
                @updateRestriction="updateRestriction"
                @showErrors="showErrors"
              />
              <SelectWorkspaces
                v-if="step === 'SELECT_WORKSPACES'"
                :show-back="!editMode"
                :restriction="restrictionForm"
                :edit-mode="editMode"
                @updateRestriction="updateRestriction"
                @showErrors="showErrors"
                @previousStep="previousStep"
              />
              <SelectContentTypes
                v-if="step === 'SELECT_CONTENT_TYPES'"
                :restriction="restrictionForm"
                :edit-mode="editMode"
                @updateRestriction="updateRestriction"
                @showErrors="showErrors"
                @previousStep="previousStep"
              />
              <SelectContents
                v-if="step === 'SELECT_CONTENTS'"
                :key="restrictionForm.project"
                :restriction="restrictionForm"
                :stepper="stepper"
                :edit-mode="editMode"
                @updateRestriction="updateRestriction"
                @showErrors="showErrors"
                @previousStep="previousStep"
              />
              <PermissionConfirmation v-if="step === 'CONFIRMATION'" :restriction="restrictionForm" @saveRestriction="saveRestriction" @previousStep="previousStep" />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { useRouter, useRoute } from 'vue2-helpers/vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';

import { useMainStore } from '../../store/index';
import { getGraphqlSchema } from '../../graphql/index';
import { getLabel, EventBus } from '@/utils/index';

import TheHeader from '../UI/TheHeader.vue';
import SelectPermission from '../changePermission/SelectPermission.vue';
import SelectWorkspaces from '../changePermission/SelectWorkspaces.vue';
import SelectContentTypes from '../changePermission/SelectContentTypes.vue';
import SelectContents from '../changePermission/SelectContents.vue';
import PermissionConfirmation from '../changePermission/PermissionConfirmation.vue';

export default {
  name: 'FormPermission',
  components: {
    TheHeader,
    SelectPermission,
    SelectWorkspaces,
    SelectContentTypes,
    SelectContents,
    PermissionConfirmation
  },
  setup () {
    const router = useRouter();

    const route = useRoute();

    const store = useMainStore();

    const stepper = ref(1);

    const errors = ref([]);

    const successes = ref([]);

    const closeAlert = () => {
      errors.value = [];
      successes.value = [];
    };

    const goBack = () => {
      router.back();
    };

    const restrictionForm = ref({
      project: null,
      type: null,
      workspaces: [],
      settings: {}
    });

    const editMode = (route.name === 'editPermission' || route.name === 'editGroupPermission');

    const isGroupRestriction = route.name === 'newGroupPermission' || route.name === 'editGroupPermission';

    const prepareRestriction = () => {
      if (editMode) {
        // get group or collaborator restriction from store
        const object = isGroupRestriction ? 'group' : 'collaborator';
        if (store[object].restrictions[route.params.index]) {
          restrictionForm.value = { ...store[object].restrictions[route.params.index] };
          if (restrictionForm.value.type === 'hide_contents' || restrictionForm.value.type === 'show_only_contents') {
            stepper.value = 3;
          } else {
            stepper.value = 2;
          }
        } else {
          const routeName = isGroupRestriction ? 'newGroupPermission' : 'newPermission';

          router.push({ name: routeName, params: { uuid: store.group.uuid } });
        }
      }
    };

    if (isGroupRestriction) {
      // Add Restriction to Group
      if (!store.group || store.group.uuid !== route.params.uuid) {
        store.loading = true;

        const variables = { uuid: route.params.uuid };

        const { onResult: onResultGroup, onError: onErrorGroup } = useQuery(getGraphqlSchema('group'), variables, { fetchPolicy: 'no-cache' });

        onResultGroup(result => {
          if (result.loading) {
            store.loading = true;
            return;
          }

          store.loading = false;

          store.group = result.data.group;
          if (!store.group.restrictions) {
            store.group.restrictions = [];
          }
          if (!store.group.readWorkspaces) {
            store.group.readWorkspaces = [];
          }
          if (!store.group.writeWorkspaces) {
            store.group.writeWorkspaces = [];
          }

          prepareRestriction();
        });

        onErrorGroup(() => {
          store.loading = false;
          store.group = null;
        });
        /** *************************** */
      } else {
        prepareRestriction();
      }
    } else if (!store.collaborator || store.collaborator.uuid !== route.params.uuid) {
      // get collaborator if null
      const variables = { uuid: route.params.uuid };

      store.loading = true;

      const { onResult: onResultCollaborator, onError: onErrorCollaborator } = useQuery(getGraphqlSchema('collaborator'), variables, { fetchPolicy: 'no-cache' });

      onResultCollaborator(result => {
        if (result.loading) {
          store.loading = true;
          return;
        }

        store.loading = false;

        store.collaborator = result.data.collaborator;
        if (!store.collaborator.restrictions) {
          store.collaborator.restrictions = [];
        }

        prepareRestriction();
      });

      onErrorCollaborator(() => {
        store.loading = false;
        store.collaborator = null;
      });
    } else {
      prepareRestriction();
    }

    const steps = computed(() => {
      let stepsList = ['SELECT_PERMISSION', 'SELECT_WORKSPACES'];

      switch (restrictionForm.value.type) {
        case 'show_only_content_types':
          // stepsList = [...stepsList, ...['SELECT_CONTENT_TYPES']];
          stepsList = [...stepsList, ...['SELECT_CONTENT_TYPES']];

          break;
        case 'restrict_content_types':
          // stepsList = [...stepsList, ...['SELECT_CONTENT_TYPES']];
          stepsList = [...stepsList, ...['SELECT_CONTENT_TYPES']];

          break;
        case 'show_only_contents':
          // stepsList = [...stepsList, ...['SELECT_CONTENT_TYPES']];
          stepsList = [...stepsList, ...['SELECT_CONTENTS']];
          break;
        case 'hide_contents':
          // stepsList = [...stepsList, ...['SELECT_CONTENT_TYPES']];
          stepsList = [...stepsList, ...['SELECT_CONTENTS']];
          break;
        default:
      }

      stepsList.push('CONFIRMATION');

      return stepsList;
    });

    const previousStep = restriction => {
      if (restriction) {
        restrictionForm.value = restriction;
      }
      stepper.value -= 1;
    };

    const nextStep = () => {
      errors.value = [];
      stepper.value += 1;
    };

    const updateRestriction = restriction => {
      restrictionForm.value = restriction;
      nextStep();
    };

    const showErrors = errorsList => {
      errors.value = errorsList;
    };

    /* Create Collaborator Restriction */
    const { mutate: mutationUpdateCollaboratorRestriction, error: sendMessageError, onError, onDone } = useMutation(
      getGraphqlSchema('updateCollaboratorRestriction'),
      () => ({
        variables:
    {
      uuid: route.params.uuid,
      restrictions: store.collaborator.restrictions
    }
      })
    );

    onError(() => {
      store.loading = false;

      successes.value = [];
      errors.value = [sendMessageError.value.message];
    });

    onDone(result => {
      store.loading = false;

      if (editMode) {
        EventBus.$emit('show-notifications', [{
          message: getLabel('restriction_edited'),
          type: 'success'
        }]);
      } else {
        EventBus.$emit('show-notifications', [{
          message: getLabel('restriction_created'),
          type: 'success'
        }]);
      }

      store.collaborator.restrictions = result.data.updateCollaborator.restrictions;
      router.push({ name: 'manageProfile', params: { uuid: route.params.uuid } });
    });

    /** **************************************************** */

    /* Update Group Restriction */
    const { mutate: mutationUpdateGroupRestriction, error: updateGroupError, onError: onUpdateGroupError, onDone: onDoneUpdateGroup } = useMutation(
      getGraphqlSchema('updateGroupRestriction'),
      () => ({
        variables:
    {
      uuid: route.params.uuid,
      restrictions: store.group.restrictions
    }
      })
    );

    onUpdateGroupError(() => {
      store.loading = false;

      successes.value = [];
      errors.value = [updateGroupError.value.message];
    });

    onDoneUpdateGroup(result => {
      store.loading = false;

      if (editMode) {
        EventBus.$emit('show-notifications', [{
          message: getLabel('restriction_edited'),
          type: 'success'
        }]);
      } else {
        EventBus.$emit('show-notifications', [{
          message: getLabel('restriction_created'),
          type: 'success'
        }]);
        EventBus.$emit('update-group', {
          uuid: store.group.uuid,
          restrictions_count: store.group.restrictions.length
        });
      }

      store.group.restrictions = result.data.updateGroup.restrictions;
      router.push({ name: 'manageGroup', params: { uuid: route.params.uuid } });
    });

    /** **************************************************** */

    const saveRestriction = () => {
      if (isGroupRestriction) {
        // Create/Change Restriction for a group

        if (editMode) {
          store.group.restrictions[route.params.index] = { ...restrictionForm.value };
        } else {
          store.group.restrictions.push({ ...restrictionForm.value });
        }

        store.loading = true;

        mutationUpdateGroupRestriction();
      } else {
        // Create/Change Restriction for a collaborator
        if (editMode) {
          store.collaborator.restrictions[route.params.index] = { ...restrictionForm.value };
        } else {
          store.collaborator.restrictions.push({ ...restrictionForm.value });
        }

        store.loading = true;

        mutationUpdateCollaboratorRestriction();
      }
    };

    const cancelEdit = () => {
      router.back();
    };

    const loadingForm = computed(() => store.loading);

    return {
      getLabel,
      loadingForm,
      stepper,
      errors,
      successes,
      closeAlert,
      goBack,
      restrictionForm,
      editMode,
      steps,
      previousStep,
      nextStep,
      updateRestriction,
      showErrors,
      saveRestriction,
      cancelEdit
    };
  }
};

</script>
